import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, Share2, FileDown } from "lucide-react";
import api, { inr, fmtDate, imgUrl } from "@/lib/api";

export const ReferralCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/referrals/my").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const link = `${window.location.origin}/register?ref=${data.code}`;
  const copy = async (text, label) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };
  const share = async () => {
    const payload = { title: "MetrixWorks", text: `Invest with MetrixWorks — use my referral code ${data.code}`, url: link };
    if (navigator.share) {
      try { await navigator.share(payload); } catch { /* cancelled */ }
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${payload.text} ${link}`)}`, "_blank", "noopener");
    }
  };
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="referral-card">
      <div className="flex items-center gap-2 mb-4">
        <Gift size={14} className="text-[#D4AF37]" />
        <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Refer & Earn</p>
      </div>
      <p className="text-sm text-white/50 leading-relaxed" data-testid="referral-rule-text">
        {data.rule?.enabled
          ? <>Earn <span className="text-[#D4AF37]">{data.rule.percent}%</span> of your friend's first approved investment. Share your code — the bonus is credited by our desk once their investment is approved.</>
          : "Share your code with friends. When they register and invest, it's tracked here."}
      </p>
      <div className="mt-5 flex items-center justify-between border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-4 py-3">
        <span className="font-mono text-lg tracking-[0.3em] text-[#D4AF37]" data-testid="referral-code">{data.code}</span>
        <div className="flex gap-3">
          <button data-testid="referral-copy-code" onClick={() => copy(data.code, "Referral code")} aria-label="Copy code" className="text-white/50 hover:text-[#D4AF37] transition-colors"><Copy size={15} /></button>
          <button data-testid="referral-copy-link" onClick={() => copy(link, "Referral link")} aria-label="Copy link" className="text-white/50 hover:text-[#D4AF37] transition-colors"><Share2 size={15} /></button>
        </div>
      </div>
      <button data-testid="referral-share-button" onClick={share} className="mt-3 w-full border border-white/15 py-2.5 font-accent text-[10px] tracking-[0.2em] uppercase text-white/60 hover:border-[#25D366] hover:text-[#25D366] transition-colors">
        Share on WhatsApp
      </button>
      <div className="mt-5 grid grid-cols-2 gap-4 border-t border-white/10 pt-4">
        <div>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">Sign-ups</p>
          <p className="font-display text-2xl text-white mt-1" data-testid="referral-count">{data.referrals.length}</p>
        </div>
        <div>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">They invested</p>
          <p className="font-display text-2xl text-[#D4AF37] mt-1" data-testid="referral-invested">{inr(data.total_invested)}</p>
        </div>
        <div>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">Bonus credited</p>
          <p className="font-display text-2xl text-emerald-400 mt-1" data-testid="referral-bonus-credited">{inr(data.bonus_credited || 0)}</p>
        </div>
        <div>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">Bonus pending</p>
          <p className="font-display text-2xl text-white/70 mt-1" data-testid="referral-bonus-pending">{inr(data.bonus_pending || 0)}</p>
        </div>
      </div>
      {data.bonuses?.length > 0 && (
        <ul className="mt-4 space-y-2 border-t border-white/10 pt-4" data-testid="referral-bonus-list">
          {data.bonuses.map((b) => (
            <li key={b.id} className="flex justify-between gap-3 text-xs text-white/60" data-testid={`referral-bonus-${b.id}`}>
              <span>{b.referred_name} · {b.percent}% of {inr(b.investment_amount)}</span>
              <span className={`text-right ${b.status === "credited" ? "text-emerald-400" : "text-white/35"}`}>
                {inr(b.bonus_amount)} · {b.status}
                {b.proof && <> · <a href={imgUrl(b.proof)} target="_blank" rel="noopener noreferrer" data-testid={`referral-bonus-proof-${b.id}`} className="text-[#D4AF37] hover:underline">View proof</a></>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {data.referrals.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="referral-list">
          {data.referrals.map((r) => (
            <li key={r.id} className="flex justify-between text-xs text-white/60">
              <span>{r.name}</span>
              <span className="text-white/35">{fmtDate(r.created_at).split(",")[0]} · {inr(r.invested)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const StatementButton = () => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data, headers } = await api.get("/investments/statement.pdf", { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "MetrixWorks-Statement.pdf";
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch {
      toast.error("Could not generate statement. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid="statement-download-button" onClick={download} disabled={busy} className="flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 font-accent text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">
      <FileDown size={13} /> {busy ? "Preparing…" : "Download Statement (PDF)"}
    </button>
  );
};
